import type { Item } from 'eolib';
import { createPortal } from 'preact/compat';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import type { IconType } from 'react-icons';
import { FaCoins, FaSearch, FaTrash } from 'react-icons/fa';
import {
  GiBroadsword,
  GiCrystalCluster,
  GiCrystalShine,
  GiCubes,
  GiCutDiamond,
  GiHealthPotion,
  GiScrollQuill,
  GiShield,
} from 'react-icons/gi';
import {
  CombatElement,
  combatElementColor,
  combatElementLabel,
} from '@/combat-overlay';
import { GOLD_ITEM_ID } from '@/consts';
import { type InstanceItem, instanceRarityLabel } from '@/controllers';
import { getEquipmentSlotForItemType } from '@/equipment';
import { playSfxById, SfxId } from '@/sfx';
import { UI_ITEM_BG, UI_PANEL_BORDER } from '@/ui/consts';
import { useClient, useLocale } from '@/ui/context';
import { SlotType } from '@/ui/enums';
import { useItemDrag } from '@/ui/in-game';
import {
  attackSpeedLine,
  categoryForItemType,
  emptyItemFilter,
  formatInstanceBaseStats,
  formatInstanceMod,
  getItemMeta,
  INVENTORY_TAB_COUNT,
  InventoryTab,
  instanceMatches,
  inventoryTabForItemType,
  isItemFilterActive,
  rangeLine,
  stackableMatches,
} from '@/utils';
import { Button } from './button';
import { type ContextMenuItem, useContextMenu } from './context-menu';
import { InventoryFilterBar } from './inventory-filter-bar';
import { ItemIcon } from './item-icon';
import { rarityBorderClass, rarityTextClass } from './rarity-style';

const TAB_ICONS: IconType[] = [
  GiBroadsword,
  GiShield,
  GiHealthPotion,
  GiCubes,
  GiScrollQuill,
];

const DOUBLE_TAP_MS = 250;
const CARD_SIZE_PX = 56;

type JunkDropZoneProps = { zone: 'drop' | 'junk' };

function JunkDropZone({ zone }: JunkDropZoneProps) {
  const { currentDrag } = useItemDrag();
  const { locale } = useLocale();
  const [isOver, setIsOver] = useState(false);
  if (currentDrag?.source !== 'inventory') return null;
  return (
    <div
      data-junk-drop={zone}
      class={`flex h-full cursor-pointer items-center gap-1 whitespace-nowrap rounded border-2 border-dashed px-2 py-0.5 text-xs transition-colors ${isOver ? 'border-error bg-error/20 text-error' : 'border-base-content/20 text-base-content/40'}`}
      onPointerEnter={() => setIsOver(true)}
      onPointerLeave={() => setIsOver(false)}
    >
      <FaTrash size={11} />
      {zone === 'junk'
        ? locale.inventory.junkDropZone
        : locale.inventory.dropZone}
    </div>
  );
}

type StackableCardProps = {
  item: Item;
  isDragging: boolean;
  /** Batch salvage select mode is active; stackables can't be salvaged, so
   *  they go inert (no drag, no context menu) rather than pretending to
   *  participate in selection. */
  selectMode: boolean;
  onPointerDown: (e: PointerEvent, item: Item, target: HTMLElement) => void;
  onRightClick: (e: MouseEvent, item: Item) => void;
  onTooltipShow: (key: string, x: number, y: number) => void;
  onTooltipHide: (key: string) => void;
};

function StackableCard({
  item,
  isDragging,
  selectMode,
  onPointerDown,
  onRightClick,
  onTooltipShow,
  onTooltipHide,
}: StackableCardProps) {
  const client = useClient();
  const { locale } = useLocale();
  const record = client.getEifRecordById(item.id);
  const graphicId = record?.graphicId ?? null;
  const name =
    record?.name ??
    locale.shared.itemFallbackName.replace('{id}', String(item.id));
  const tooltipKey = `stack:${item.id}`;

  const handlePointerDown = (e: PointerEvent) => {
    if (selectMode) return;
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    e.preventDefault();
    onPointerDown(e, item, e.currentTarget as HTMLElement);
  };

  const showTooltip = (e: MouseEvent) => {
    const el = e.currentTarget as HTMLElement;
    const r = el.getBoundingClientRect();
    const x =
      r.right + 4 + 200 > window.innerWidth ? r.left - 4 - 200 : r.right + 4;
    const y = Math.min(Math.max(r.top, 4), window.innerHeight - 80);
    onTooltipShow(tooltipKey, x, y);
  };

  return (
    <div
      data-inventory-item={item.id}
      class={`flex select-none flex-col items-center gap-2 rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-2 transition-colors${selectMode ? ' cursor-not-allowed opacity-50' : ' cursor-grab hover:bg-base-content/10 active:bg-base-content/15'}${isDragging ? ' opacity-30' : ''}`}
      onPointerDown={handlePointerDown}
      onContextMenu={(e) => {
        e.preventDefault();
        if (selectMode) return;
        onRightClick(e, item);
      }}
      onMouseEnter={showTooltip}
      onMouseLeave={() => onTooltipHide(tooltipKey)}
    >
      <div
        class={`flex h-14 w-14 items-center justify-center overflow-hidden rounded border ${UI_PANEL_BORDER} bg-base-300 p-1.5 lg:h-16 lg:w-16`}
      >
        {graphicId === null ? (
          <div class='h-10 w-10 rounded bg-base-content/10' />
        ) : (
          <ItemIcon
            graphicId={graphicId}
            alt={name}
            class='h-10 w-10 object-contain lg:h-12 lg:w-12'
          />
        )}
      </div>
      <div class='flex w-full flex-col items-center gap-0.5'>
        <span class='wrap-break-word line-clamp-2 w-full text-center font-medium text-xs leading-tight'>
          {name}
        </span>
        {item.amount > 1 && (
          <span class='text-[10px] tabular-nums opacity-60'>
            x{item.amount.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

type InstanceCardProps = {
  inst: InstanceItem;
  isDragging: boolean;
  /** Batch salvage select mode: clicking toggles selection instead of
   *  starting a drag. */
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: (instanceId: string) => void;
  onPointerDown: (
    e: PointerEvent,
    inst: InstanceItem,
    target: HTMLElement,
  ) => void;
  onRightClick: (e: MouseEvent, inst: InstanceItem) => void;
  onTooltipShow: (key: string, x: number, y: number) => void;
  onTooltipHide: (key: string) => void;
};

function InstanceCard({
  inst,
  isDragging,
  selectMode,
  selected,
  onToggleSelect,
  onPointerDown,
  onRightClick,
  onTooltipShow,
  onTooltipHide,
}: InstanceCardProps) {
  const client = useClient();
  const { locale } = useLocale();
  const record = client.getEifRecordById(inst.baseItemId);
  const graphicId = record?.graphicId ?? null;
  const name =
    record?.name ??
    locale.shared.itemFallbackName.replace('{id}', String(inst.baseItemId));
  const tooltipKey = `instance:${inst.instanceId}`;
  const borderClass = rarityBorderClass(inst.rarity);
  const textClass = rarityTextClass(inst.rarity);
  const ringClass = selected ? 'ring-2 ring-primary' : '';

  const handlePointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    e.preventDefault();
    if (selectMode) {
      onToggleSelect(inst.instanceId);
      return;
    }
    onPointerDown(e, inst, e.currentTarget as HTMLElement);
  };

  const showTooltip = (e: MouseEvent) => {
    const el = e.currentTarget as HTMLElement;
    const r = el.getBoundingClientRect();
    const x =
      r.right + 4 + 240 > window.innerWidth ? r.left - 4 - 240 : r.right + 4;
    const y = Math.min(Math.max(r.top, 4), window.innerHeight - 100);
    onTooltipShow(tooltipKey, x, y);
  };

  return (
    <div
      data-instance-item={inst.instanceId}
      class={`flex select-none flex-col items-center gap-2 rounded border-2 ${borderClass} ${ringClass} ${UI_ITEM_BG} p-2 transition-colors hover:bg-base-content/10${selectMode ? ' cursor-pointer' : ' cursor-grab'}${isDragging ? ' opacity-30' : ''}`}
      onPointerDown={handlePointerDown}
      onContextMenu={(e) => {
        if (selectMode) return;
        e.preventDefault();
        onRightClick(e, inst);
      }}
      onMouseEnter={showTooltip}
      onMouseLeave={() => onTooltipHide(tooltipKey)}
    >
      <div
        class={`flex h-14 w-14 items-center justify-center overflow-hidden rounded border ${UI_PANEL_BORDER} bg-base-300 p-1.5 lg:h-16 lg:w-16`}
      >
        {graphicId === null ? (
          <div class='h-10 w-10 rounded bg-base-content/10' />
        ) : (
          <ItemIcon
            graphicId={graphicId}
            alt={name}
            class='h-10 w-10 object-contain lg:h-12 lg:w-12'
          />
        )}
      </div>
      <div class='flex w-full flex-col items-center gap-0.5'>
        <span
          class={`wrap-break-word line-clamp-2 w-full text-center font-medium text-xs leading-tight ${textClass}`}
        >
          {name}
        </span>
        <span class='text-[10px] tabular-nums opacity-60'>
          Lv{inst.level} · {inst.mods.length} mod
          {inst.mods.length === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
}

function InstanceTooltipContent({
  inst,
  lines,
}: {
  inst: InstanceItem;
  lines: string[];
}) {
  // Layout: name (lines[0]) → element row (if non-Plain) → rest of lines.
  const [head, ...rest] = lines;
  return (
    <>
      {head !== undefined && <div class='font-semibold'>{head}</div>}
      {inst.element !== CombatElement.Plain && (
        <div
          style={{ color: combatElementColor(inst.element), fontSize: '11px' }}
        >
          {combatElementLabel(inst.element)}
        </div>
      )}
      {rest.map((line, i) => (
        <div key={i} class='opacity-70'>
          {line}
        </div>
      ))}
    </>
  );
}

/** EquipmentSlot indexes that are the "second" of a two-slot pair. */
const SUB_LOC_1_SLOTS = new Set([10, 12, 14]);
/** EquipmentSlot indexes that are the "first" of a two-slot pair
 *  (Ring1=9, Armlet1=11, Bracer1=13). */
const DUAL_PRIMARY_SLOTS = new Set([9, 11, 13]);

function subLocForSlot(slot: number): number {
  return SUB_LOC_1_SLOTS.has(slot) ? 1 : 0;
}

type Props = {
  /** If provided, only items with these IDs are shown. Defaults to all client items. */
  itemIds?: number[];
};

export function InventoryGrid({ itemIds }: Props) {
  const client = useClient();
  const { locale } = useLocale();
  const { startDrag, currentDrag } = useItemDrag();
  const [activeTab, setActiveTab] = useState<InventoryTab>(
    InventoryTab.Weapons,
  );
  const [refresh, setRefresh] = useState(0);
  const [filter, setFilter] = useState(emptyItemFilter());
  const [tooltip, setTooltip] = useState<{
    key: string;
    x: number;
    y: number;
  } | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastTapRef = useRef(0);

  const selectionYield = useMemo(() => {
    const acc = { weapon: 0, armor: 0, accessory: 0 };
    if (!selectMode) return acc;
    for (const id of selectedIds) {
      const inst = client.instanceController.byInstanceId(id);
      if (!inst) continue;
      const record = client.getEifRecordById(inst.baseItemId);
      const cat = record ? categoryForItemType(record.type) : null;
      if (!cat) continue;
      acc[cat] += client.salvageController.yieldFor(inst.level, inst.rarity);
    }
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, selectMode, selectedIds, refresh]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      const target = e.target as HTMLElement;
      if (
        !target.closest('[data-inventory-item]') &&
        !target.closest('[data-instance-item]')
      ) {
        setTooltip(null);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    const handler = () => setRefresh((n) => n + 1);
    client.inventoryController.subscribeInventoryChanged(handler);
    client.instanceController.subscribeChanged(handler);
    client.tradeController.subscribe(handler);
    client.salvageController.subscribeChanged(handler);
    return () => {
      client.inventoryController.unsubscribeInventoryChanged(handler);
      client.instanceController.unsubscribeChanged(handler);
      client.tradeController.unsubscribe(handler);
      client.salvageController.unsubscribeChanged(handler);
    };
  }, [client]);

  const visibleItems = useMemo((): Item[] => {
    const source = itemIds
      ? client.inventoryController.items.filter((i) => itemIds.includes(i.id))
      : client.inventoryController.items;
    return source.filter((i) => i.id !== GOLD_ITEM_ID);
    // `refresh` is the version counter bumped by the controller
    // subscriptions below; without it the memo returned a stale
    // snapshot after every inventory mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, itemIds, currentDrag, refresh]);

  const visibleInstances = useMemo((): InstanceItem[] => {
    // Inventory grid shows un-equipped, un-lockered instances — and
    // excludes anything the player has offered into a trade so the
    // same card doesn't appear in both inventory and trade panes.
    const all = client.instanceController.inventoryList();
    const inTrade = client.tradeController.playerOfferedInstanceIds();
    const filtered = all.filter((i) => !inTrade.has(i.instanceId));
    return itemIds
      ? filtered.filter((i) => itemIds.includes(i.baseItemId))
      : filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, itemIds, currentDrag, refresh]);

  const itemsByTab = useMemo(() => {
    const buckets: Item[][] = Array.from(
      { length: INVENTORY_TAB_COUNT },
      () => [],
    );
    for (const item of visibleItems) {
      const record = client.getEifRecordById(item.id);
      const tab = record
        ? inventoryTabForItemType(record.type)
        : InventoryTab.Materials;
      buckets[tab].push(item);
    }
    return buckets;
  }, [visibleItems, client]);

  const instancesByTab = useMemo(() => {
    const buckets: InstanceItem[][] = Array.from(
      { length: INVENTORY_TAB_COUNT },
      () => [],
    );
    for (const inst of visibleInstances) {
      const record = client.getEifRecordById(inst.baseItemId);
      const tab = record
        ? inventoryTabForItemType(record.type)
        : InventoryTab.Materials;
      buckets[tab].push(inst);
    }
    // Show higher-rarity instances first; level breaks ties.
    for (const bucket of buckets) {
      bucket.sort((a, b) => b.rarity - a.rarity || b.level - a.level);
    }
    return buckets;
  }, [visibleInstances, client]);

  // When a filter is active the grid drops the per-tab split and shows
  // every match across all categories in one list (the intent is "find
  // items with X", not "browse one tab"). Empty when no filter is set.
  const filteredInstances = useMemo(() => {
    if (!isItemFilterActive(filter)) return [];
    return visibleInstances
      .filter((inst) =>
        instanceMatches(inst, client.getEifRecordById(inst.baseItemId), filter),
      )
      .sort((a, b) => b.rarity - a.rarity || b.level - a.level);
  }, [visibleInstances, filter, client]);

  const filteredItems = useMemo(() => {
    if (!isItemFilterActive(filter)) return [];
    return visibleItems.filter((i) =>
      stackableMatches(client.getEifRecordById(i.id), filter),
    );
  }, [visibleItems, filter, client]);

  const tabCounts = itemsByTab.map(
    (b, i) => b.length + instancesByTab[i].length,
  );
  const activeItems = itemsByTab[activeTab];
  const activeInstances = instancesByTab[activeTab];
  const goldAmount = client.inventoryController.goldAmount;
  const shards = useMemo(
    () => client.salvageController.shardCounts,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, refresh],
  );

  const getCoords = () => {
    const p = client.getPlayerCharacter();
    return p ? { x: p.coords.x, y: p.coords.y } : { x: 0, y: 0 };
  };

  const handleItemPointerDown = useCallback(
    (e: PointerEvent, item: Item, target: HTMLElement) => {
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        lastTapRef.current = 0;
        client.inventoryController.useItem(item.id);
        return;
      }
      lastTapRef.current = now;

      const rect = target.getBoundingClientRect();
      const record = client.getEifRecordById(item.id);
      if (!record) return;

      let longPressTimer: ReturnType<typeof setTimeout> | null = null;
      if (e.pointerType === 'touch') {
        const tooltipX = Math.min(rect.right + 4, window.innerWidth - 168);
        const tooltipY = Math.max(4, rect.top);
        setTooltip({ key: `stack:${item.id}`, x: tooltipX, y: tooltipY });
        longPressTimer = setTimeout(() => {
          longPressTimer = null;
          setTooltip(null);
        }, 700);
      } else {
        setTooltip(null);
      }

      playSfxById(SfxId.InventoryPickup);

      startDrag({
        element: target,
        info: {
          source: 'inventory',
          itemId: item.id,
          pointerId: e.pointerId,
          ghostX: e.clientX,
          ghostY: e.clientY,
          // Center the fixed-size ghost on the cursor. The source card is
          // much larger than CARD_SIZE_PX (icon + name + quantity + padding),
          // so measuring the grab offset against it would shove the ghost left
          // of the pointer.
          offsetX: CARD_SIZE_PX / 2,
          offsetY: CARD_SIZE_PX / 2,
          graphicId: record.graphicId,
          ghostWidth: CARD_SIZE_PX,
          ghostHeight: CARD_SIZE_PX,
        },
        onMove: (mx, my) => {
          if (
            longPressTimer &&
            Math.hypot(mx - e.clientX, my - e.clientY) > 5
          ) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          setTooltip(null);
          if (client.app) {
            const canvas = client.app.renderer.canvas;
            const canvasRect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / canvasRect.width;
            const scaleY = canvas.height / canvasRect.height;
            client.setMousePosition({
              x: Math.min(
                Math.max(Math.floor((mx - canvasRect.left) * scaleX), 0),
                canvas.width,
              ),
              y: Math.min(
                Math.max(Math.floor((my - canvasRect.top) * scaleY), 0),
                canvas.height,
              ),
            });
          }
        },
        onResolve: (result) => {
          playSfxById(SfxId.InventoryPlace);
          if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          if (result.type === 'cancelled') return;
          if (result.type === 'cell' || result.type === 'tab') {
            return;
          }
          if (result.type === 'equip-slot') {
            client.inventoryController.equipItem(result.slot, item.id);
          } else if (result.type === 'hotbar-slot') {
            client.hotbarController.setSlot(result.index, {
              type: SlotType.Item,
              typeId: item.id,
            });
          } else if (result.type === 'ground') {
            // pointer-captured drags don't fire mousemove on the canvas,
            // so client.mouseCoords is stale at pointerup. Refresh from
            // the resolve-time client coords before checking range.
            const zoom = client.viewportController?.getZoom?.() ?? 1;
            client.setMousePosition({
              x: result.clientX / zoom,
              y: result.clientY / zoom,
            });
            if (!client.mapController.cursorInDropRange()) return;
            const coords = client.mouseCoords ?? getCoords();
            client.inventoryController.dropItem(item.id, coords);
          } else if (result.type === 'junk') {
            client.inventoryController.junkItem(item.id);
          } else if (result.type === 'drop') {
            const coords = client.getPlayerCharacter()?.coords;
            if (coords) client.inventoryController.dropItem(item.id, coords);
          } else if (result.type === 'chest') {
            client.chestController.addItem(item.id);
          } else if (result.type === 'locker') {
            client.lockerController.addItem(item.id);
          } else if (result.type === 'trade') {
            const invItem = client.inventoryController.getItemById(item.id);
            if (!invItem) return;
            if (invItem.amount > 1) {
              const itemName =
                client.getEifRecordById(item.id)?.name ?? String(item.id);
              client.alertController.showAmount(
                locale.trade.dropHowMany,
                itemName,
                invItem.amount,
                locale.trade.drop,
                (amount) => {
                  if (amount !== null && amount > 0) {
                    client.tradeController.addItem(item.id, amount);
                  }
                },
              );
            } else {
              client.tradeController.addItem(item.id, 1);
            }
          }
        },
      });
    },
    [client, locale, startDrag],
  );

  const handleInstancePointerDown = useCallback(
    (e: PointerEvent, inst: InstanceItem, target: HTMLElement) => {
      const record = client.getEifRecordById(inst.baseItemId);
      const graphicId = record?.graphicId ?? 0;
      playSfxById(SfxId.InventoryPickup);
      setTooltip(null);
      startDrag({
        element: target,
        info: {
          source: 'inventory',
          itemId: inst.baseItemId,
          instanceId: inst.instanceId,
          pointerId: e.pointerId,
          ghostX: e.clientX,
          ghostY: e.clientY,
          // Center the fixed-size ghost on the cursor (see the base-item
          // handler above — the source card dwarfs CARD_SIZE_PX).
          offsetX: CARD_SIZE_PX / 2,
          offsetY: CARD_SIZE_PX / 2,
          graphicId,
          ghostWidth: CARD_SIZE_PX,
          ghostHeight: CARD_SIZE_PX,
        },
        onResolve: (result) => {
          playSfxById(SfxId.InventoryPlace);
          if (result.type === 'cancelled') return;
          if (result.type === 'equip-slot') {
            // Mirror the classic equip path's requirement pre-check so a
            // rolled item that fails level/stat/class/gender gates shows
            // a toast instead of being silently dropped by the server
            // (the instance equip packet has no reject-reply).
            const record = client.getEifRecordById(inst.baseItemId);
            if (
              record &&
              !client.inventoryController.checkEquipRequirements(record)
            ) {
              return;
            }
            client.instanceController.sendEquip(
              inst.instanceId,
              subLocForSlot(result.slot),
            );
          } else if (result.type === 'locker') {
            client.instanceController.sendLockerDeposit(inst.instanceId);
          } else if (result.type === 'chest') {
            client.instanceController.sendChestDeposit(inst.instanceId);
          } else if (result.type === 'trade') {
            client.instanceController.sendTradeAdd(inst.instanceId);
          } else if (result.type === 'enchanter') {
            client.enchanterController.selectInstance(inst.instanceId);
          } else if (result.type === 'ground') {
            // pointer-captured drags don't fire mousemove on the canvas,
            // so client.mouseCoords is stale at pointerup. Refresh from
            // the resolve-time client coords before checking range.
            const zoom = client.viewportController?.getZoom?.() ?? 1;
            client.setMousePosition({
              x: result.clientX / zoom,
              y: result.clientY / zoom,
            });
            if (!client.mapController.cursorInDropRange()) return;
            const coords = client.mouseCoords ?? getCoords();
            // ByteCoords wire: tile + 1, clamped to [1, 252] (eolib char max).
            const wx = Math.max(1, Math.min(252, coords.x + 1));
            const wy = Math.max(1, Math.min(252, coords.y + 1));
            client.instanceController.sendKick(inst.instanceId, wx, wy);
          } else if (result.type === 'drop') {
            // Dropped on the "Drop Item" zone — resolve to the player's
            // own tile coords client-side. eolib `char` maxes at 252, so
            // the OG-client 0xFF "at feet" sentinel can't fit on the wire.
            const playerCoords = client.getPlayerCoords();
            if (!playerCoords) return;
            const wx = Math.max(1, Math.min(252, playerCoords.x + 1));
            const wy = Math.max(1, Math.min(252, playerCoords.y + 1));
            client.instanceController.sendKick(inst.instanceId, wx, wy);
          } else if (result.type === 'junk') {
            const record = client.getEifRecordById(inst.baseItemId);
            const itemName =
              record?.name ??
              locale.shared.itemFallbackName.replace(
                '{id}',
                String(inst.baseItemId),
              );
            const message = locale.inventory.junkConfirmMessage.replace(
              '{name}',
              itemName,
            );
            client.alertController.showConfirm(
              locale.inventory.junkConfirmTitle,
              message,
              (confirmed) => {
                if (confirmed) {
                  client.instanceController.sendJunk(inst.instanceId);
                }
              },
            );
          }
        },
      });
    },
    [client, startDrag],
  );

  const { open: openContextMenu } = useContextMenu();

  /**
   * Resolve the sub-slot for a dual-slot item type (Ring/Armlet/Bracer).
   * Mirrors InventoryController.useItem exactly: first sub-slot when it's
   * empty, otherwise the second (fills the open finger if there is one, else
   * swaps the second) — so right-click and double-click agree.
   */
  const dualSubLocForType = useCallback(
    (slot1: number): number => {
      const equipment = client.inventoryController.getEquipmentArray();
      return equipment[slot1] ? 1 : 0;
    },
    [client],
  );

  const handleStackableRightClick = useCallback(
    (e: MouseEvent, item: Item) => {
      const record = client.getEifRecordById(item.id);
      if (!record) return;
      const items: ContextMenuItem[] = [];
      const slot = getEquipmentSlotForItemType(record.type);
      if (slot !== undefined) {
        items.push({
          label: 'Equip',
          onClick: () => {
            // For dual-slot primary slots, pick the open sub-slot when possible.
            let resolvedSlot = slot;
            if (DUAL_PRIMARY_SLOTS.has(slot)) {
              resolvedSlot = dualSubLocForType(slot) === 1 ? slot + 1 : slot;
            }
            client.inventoryController.equipItem(resolvedSlot, item.id);
          },
        });
      }
      items.push({
        label: 'Item info',
        onClick: () => client.lookupController.requestItem(item.id),
      });
      openContextMenu(e.clientX, e.clientY, items);
    },
    [client, dualSubLocForType, openContextMenu],
  );

  const handleInstanceRightClick = useCallback(
    (e: MouseEvent, inst: InstanceItem) => {
      const record = client.getEifRecordById(inst.baseItemId);
      if (!record) return;
      const items: ContextMenuItem[] = [];
      const slot = getEquipmentSlotForItemType(record.type);
      if (slot !== undefined) {
        items.push({
          label: 'Equip',
          onClick: () => {
            // Requirement pre-check mirrors the drag path (instance equip has no server reject reply).
            if (!client.inventoryController.checkEquipRequirements(record)) {
              return;
            }
            // Dual-slot handling: pick the open sub-slot.
            const subLoc = DUAL_PRIMARY_SLOTS.has(slot)
              ? dualSubLocForType(slot)
              : 0;
            client.instanceController.sendEquip(inst.instanceId, subLoc);
          },
        });
      }
      items.push({
        label: 'Item info',
        onClick: () => client.lookupController.requestItem(inst.baseItemId),
      });
      items.push({
        label: locale.inventory.salvage,
        onClick: () => {
          const itemName =
            record.name ||
            locale.shared.itemFallbackName.replace(
              '{id}',
              String(inst.baseItemId),
            );
          const shards = client.salvageController.yieldFor(
            inst.level,
            inst.rarity,
          );
          const message = locale.inventory.salvageConfirmMessage
            .replace('{name}', itemName)
            .replace('{shards}', String(shards));
          client.alertController.showConfirm(
            locale.inventory.salvage,
            message,
            (confirmed) => {
              if (confirmed) {
                client.salvageController.sendSalvage([inst.instanceId]);
              }
            },
          );
        },
      });
      openContextMenu(e.clientX, e.clientY, items);
    },
    [client, dualSubLocForType, locale, openContextMenu],
  );

  const onTooltipShow = useCallback((key: string, x: number, y: number) => {
    setTooltip({ key, x, y });
  }, []);

  const onTooltipHide = useCallback((key: string) => {
    setTooltip((t) => (t?.key === key ? null : t));
  }, []);

  const toggleSelectMode = useCallback(() => {
    setSelectMode((m) => !m);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = useCallback((instanceId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  }, []);

  const cancelSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const confirmSalvageSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    const count = ids.length;
    const shards =
      selectionYield.weapon + selectionYield.armor + selectionYield.accessory;
    const message = locale.inventory.salvageConfirm
      .replace('{count}', String(count))
      .replace('{shards}', String(shards));
    client.alertController.showConfirm(
      locale.inventory.salvage,
      message,
      (confirmed) => {
        if (confirmed) {
          client.salvageController.sendSalvage(ids);
          setSelectMode(false);
          setSelectedIds(new Set());
        }
      },
    );
  }, [client, locale, selectedIds, selectionYield]);

  const TAB_LABELS = [
    locale.inventory.tabWeapons,
    locale.inventory.tabArmor,
    locale.inventory.tabConsumables,
    locale.inventory.tabMaterials,
    locale.inventory.tabQuestItems,
  ];

  const getTooltipLines = (key: string): string[] => {
    if (key.startsWith('stack:')) {
      const id = Number(key.slice(6));
      const item = visibleItems.find((i) => i.id === id);
      const record = item ? client.getEifRecordById(item.id) : undefined;
      if (!item || !record) return [];
      const meta = getItemMeta(record);
      const qty = item.amount > 1 ? ` x${item.amount.toLocaleString()}` : '';
      const lines = [record.name + qty, ...meta];
      const speed = attackSpeedLine(client, item.id);
      if (speed) {
        lines.push(speed);
      }
      const range = rangeLine(client, item.id);
      if (range) {
        lines.push(range);
      }
      return lines;
    }
    if (key.startsWith('instance:')) {
      const id = key.slice(9);
      const inst = visibleInstances.find((i) => i.instanceId === id);
      if (!inst) return [];
      const record = client.getEifRecordById(inst.baseItemId);
      const name = record?.name ?? `Item ${inst.baseItemId}`;
      const lines = [`${name} (${instanceRarityLabel(inst.rarity)})`];
      const category = record ? categoryForItemType(record.type) : null;
      if (category) {
        lines.push(
          { weapon: 'Weapon', armor: 'Armor', accessory: 'Accessory' }[
            category
          ],
        );
      }
      lines.push(`Level ${inst.level}`);
      const speed = attackSpeedLine(client, inst.baseItemId);
      if (speed) {
        lines.push(speed);
      }
      const range = rangeLine(client, inst.baseItemId);
      if (range) {
        lines.push(range);
      }
      if (record) {
        lines.push(...formatInstanceBaseStats(record, inst.level));
      }
      for (const m of inst.mods) {
        lines.push(formatInstanceMod(m));
      }
      return lines;
    }
    return [];
  };

  const filterActive = isItemFilterActive(filter);
  const displayInstances = filterActive ? filteredInstances : activeInstances;
  const displayItems = filterActive ? filteredItems : activeItems;
  const isEmpty = displayInstances.length === 0 && displayItems.length === 0;

  return (
    <div class='flex flex-col gap-1'>
      <div class='flex items-center justify-between gap-1 px-1'>
        <div class='flex items-center gap-1 text-warning text-xs'>
          <FaCoins size={11} />
          <span class='font-semibold tabular-nums'>
            {goldAmount.toLocaleString()}
          </span>
        </div>
        <div class='flex items-center gap-1'>
          <Button
            variant={selectMode ? ['sm', 'primary'] : ['sm']}
            onClick={toggleSelectMode}
          >
            {locale.inventory.salvage}
          </Button>
          <JunkDropZone zone='drop' />
          <JunkDropZone zone='junk' />
        </div>
      </div>

      <InventoryFilterBar filter={filter} onChange={setFilter} />

      <div class='flex gap-1'>
        {!filterActive && (
          <div
            role='tablist'
            aria-orientation='vertical'
            class='flex flex-col gap-1'
          >
            {TAB_LABELS.map((label, index) => {
              const Icon = TAB_ICONS[index];
              const isActive = index === activeTab;
              return (
                <button
                  key={label}
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  aria-label={`${label} (${tabCounts[index]})`}
                  title={label}
                  onClick={() => setActiveTab(index as InventoryTab)}
                  class={`flex h-11 w-11 flex-col items-center justify-center rounded border transition-colors ${
                    isActive
                      ? `${UI_PANEL_BORDER} bg-primary/20 text-primary`
                      : `${UI_PANEL_BORDER} ${UI_ITEM_BG} text-base-content/70 hover:bg-base-content/10`
                  }`}
                >
                  <Icon size={16} />
                  <span class='text-[10px] tabular-nums leading-tight'>
                    {tabCounts[index]}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div
          class={`flex max-h-72 min-h-48 flex-1 flex-col overflow-hidden rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG}`}
        >
          <div
            class={`flex items-center gap-1 border-b ${UI_PANEL_BORDER} px-2 py-1 text-xs`}
          >
            {filterActive ? (
              <>
                <FaSearch size={11} />
                <span class='font-semibold'>
                  {locale.inventory.filterResults}
                </span>
                <span class='ml-auto tabular-nums opacity-60'>
                  {displayInstances.length + displayItems.length}
                </span>
              </>
            ) : (
              <>
                {(() => {
                  const Icon = TAB_ICONS[activeTab];
                  return <Icon size={11} />;
                })()}
                <span class='font-semibold'>{TAB_LABELS[activeTab]}</span>
                <span class='ml-auto tabular-nums opacity-60'>
                  {tabCounts[activeTab]}
                </span>
              </>
            )}
          </div>
          <div class='flex-1 overflow-y-auto p-2'>
            {isEmpty ? (
              <p class='py-4 text-center text-sm opacity-50'>
                {filterActive
                  ? locale.inventory.filterNoMatches
                  : locale.inventory.empty}
              </p>
            ) : (
              <div class='grid grid-cols-2 gap-2 lg:grid-cols-3'>
                {displayInstances.map((inst) => (
                  <InstanceCard
                    key={inst.instanceId}
                    inst={inst}
                    isDragging={
                      currentDrag?.source === 'inventory' &&
                      currentDrag?.instanceId === inst.instanceId
                    }
                    selectMode={selectMode}
                    selected={selectedIds.has(inst.instanceId)}
                    onToggleSelect={toggleSelect}
                    onPointerDown={handleInstancePointerDown}
                    onRightClick={handleInstanceRightClick}
                    onTooltipShow={onTooltipShow}
                    onTooltipHide={onTooltipHide}
                  />
                ))}
                {displayItems.map((item) => (
                  <StackableCard
                    key={item.id}
                    item={item}
                    isDragging={
                      currentDrag?.source === 'inventory' &&
                      currentDrag.itemId === item.id
                    }
                    selectMode={selectMode}
                    onPointerDown={handleItemPointerDown}
                    onRightClick={handleStackableRightClick}
                    onTooltipShow={onTooltipShow}
                    onTooltipHide={onTooltipHide}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectMode && (
        <div
          class={`mt-1 flex items-center justify-between gap-2 rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} px-2 py-1 text-xs`}
        >
          <span class='tabular-nums'>
            {locale.inventory.salvageSelectedCount.replace(
              '{count}',
              String(selectedIds.size),
            )}
          </span>
          <div class='flex gap-2'>
            <Button variant={['sm']} onClick={cancelSelectMode}>
              {locale.shared.btnCancel}
            </Button>
            <Button
              variant={['sm', 'primary']}
              disabled={selectedIds.size === 0}
              onClick={confirmSalvageSelected}
            >
              {locale.inventory.salvage}
            </Button>
          </div>
        </div>
      )}

      <div
        class={`mt-1 flex items-center justify-around rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} px-2 py-1 text-xs`}
      >
        <span
          class='tooltip tooltip-top flex items-center gap-1'
          data-tip={locale.inventory.shardsWeaponTooltip}
        >
          <span class='text-amber-400'>
            <GiCrystalShine size={14} />
          </span>
          <span class='font-semibold tabular-nums'>
            {shards.weapon.toLocaleString()}
          </span>
          {selectMode && selectionYield.weapon > 0 && (
            <span class='font-semibold text-success tabular-nums'>
              {' '}
              (+{selectionYield.weapon.toLocaleString()})
            </span>
          )}
        </span>
        <span
          class='tooltip tooltip-top flex items-center gap-1'
          data-tip={locale.inventory.shardsArmorTooltip}
        >
          <span class='text-sky-400'>
            <GiCrystalCluster size={14} />
          </span>
          <span class='font-semibold tabular-nums'>
            {shards.armor.toLocaleString()}
          </span>
          {selectMode && selectionYield.armor > 0 && (
            <span class='font-semibold text-success tabular-nums'>
              {' '}
              (+{selectionYield.armor.toLocaleString()})
            </span>
          )}
        </span>
        <span
          class='tooltip tooltip-top flex items-center gap-1'
          data-tip={locale.inventory.shardsAccessoryTooltip}
        >
          <span class='text-violet-400'>
            <GiCutDiamond size={14} />
          </span>
          <span class='font-semibold tabular-nums'>
            {shards.accessory.toLocaleString()}
          </span>
          {selectMode && selectionYield.accessory > 0 && (
            <span class='font-semibold text-success tabular-nums'>
              {' '}
              (+{selectionYield.accessory.toLocaleString()})
            </span>
          )}
        </span>
      </div>

      {tooltip &&
        (() => {
          const lines = getTooltipLines(tooltip.key);
          if (!lines.length) return null;
          const isInstance = tooltip.key.startsWith('instance:');
          const instId = isInstance ? tooltip.key.slice(9) : null;
          const inst = instId
            ? visibleInstances.find((i) => i.instanceId === instId)
            : null;
          return createPortal(
            <div
              class='pointer-events-none w-max max-w-56 rounded bg-base-300 px-2 py-1 text-xs shadow-lg'
              style={{
                position: 'fixed',
                left: tooltip.x,
                top: tooltip.y,
                zIndex: 9999,
              }}
            >
              {inst ? (
                <InstanceTooltipContent inst={inst} lines={lines} />
              ) : (
                lines.map((line, i) => (
                  <div key={i} class={i === 0 ? 'font-semibold' : 'opacity-70'}>
                    {line}
                  </div>
                ))
              )}
            </div>,
            document.getElementById('ui') ?? document.body,
          );
        })()}
    </div>
  );
}
